import { EstateDetail as EstateDetailType } from '@portals/sip-client-data/src/general/ApiClientTypes';

import { CellarType, HeatingType, HouseType, ModRTeaserParams } from '../../ModRTeaser/ModRTeaser';

const mapCellarStatus = (hasCellar: boolean): CellarType => {
  return hasCellar ? 'totally' : 'unavailable';
};

const heatingMap: Record<string, HeatingType> = {
  oil: 'oil_fuel',
  gas: 'natural_gas',
  electric: 'electricity',
  alternative: 'oven_wood_chips',
  solar: 'electricity',
  geothermal: 'electricity',
  airthermal: 'electricity',
  district_heating: 'district_heating_heating_plant',
  bttp: 'oven_wood_chips',
  add_water: 'electricity',
  pellets: 'oven_wood_pellets',
  coal: 'oven_wood_chips',
  wood: 'oven_wood_chips',
  liquid_gas: 'natural_gas',
};

const mapHeatingType = (heating: string | null): HeatingType => {
  if (!heating) {
    return null;
  }

  return heatingMap[heating] || null;
};

const houseTypeMap: Record<string, HouseType> = {
  single_family_house: 'free_standing',
  semi_detached_house: 'semi_detached',
  townhouse: 'row_center',
  end_unit: 'row_end',
  mid_terrace_house: 'row_center',
  corner_terrace_house: 'row_end',
  bungalow: 'free_standing',
  villa: 'free_standing',
  two_family_house: 'free_standing',
  cityhouse: 'free_standing',
  cottage: 'free_standing',
  mountain_hut: 'free_standing',
  chalet: 'free_standing',
  beach_house: 'free_standing',
  castle: 'free_standing',
  manor: 'free_standing',
  finca: 'free_standing',
  rustico: 'free_standing',
  pre_build_house: 'free_standing',
  farmhouse: 'free_standing',
  country_house: 'free_standing',
  palace: 'free_standing',
  arbour: 'free_standing',
};

const mapHouseType = (houseType: string | null): HouseType => {
  if (!houseType) {
    return null;
  }

  return houseTypeMap[houseType] || null;
};

export const getParamsForModRTeaser = (estate: EstateDetailType): ModRTeaserParams => {
  const livingSpace = estate.mainFacts.find((fact) => fact.label === 'Wohnfläche')?.value.match(/^(\d+)/)?.[0];

  const conditionData =
    estate.frontendItems
      .find((item) => item.name === 'conditionData')
      ?.contents.flatMap((content) => (content.type === 'attributeTable' ? content.data : [])) || [];

  const constructionYear = conditionData.find((entry) => entry.label === 'Baujahr')?.value;
  const heating = estate.firingType || null;
  const heatingType = mapHeatingType(heating);

  const floors = estate.frontendItems
    .find((item) => item.name === 'objectData')
    ?.contents.flatMap((content) => (content.type === 'attributeTable' ? content.data : []))
    .find((entry) => entry.label === 'Anzahl Etagen')?.value;

  const hasCellar = estate.frontendItems
    .find((item) => item.name === 'equipmentData')
    ?.contents.flatMap((content) => (content.type === 'checkedList' ? content.data : []))
    .some((item) => item.value === 'Unterkellert');
  const cellarStatus = mapCellarStatus(hasCellar);

  const estateHouseType = estate.houseType || null;
  const houseType = mapHouseType(estateHouseType);

  const params = {
    street: estate.address.street || null,
    streetNumber: estate.address.streetNumber || null,
    zip: estate.address.zip,
    city: estate.address.city,
    livingSpace: livingSpace || null,
    constructionYear: constructionYear || null,
    heating: heatingType,
    housetype: houseType,
    floors: floors || null,
    cellar: cellarStatus,
  };

  return params;
};

const isValidHouseType = (houseType: string | null): boolean => {
  if (!houseType) {
    return false;
  }
  const validHouseTypes = Object.keys(houseTypeMap);

  return validHouseTypes.includes(houseType);
};

export const isFinancableEstate = (estate: EstateDetailType): boolean => !estate.financing.hide;

export const isHouseForPurchase = (estate: EstateDetailType): boolean => {
  const isHouse = estate.objectType === 'house';
  const hasPurchasePrice = estate.frontendItems
    .filter((item) => item.name === 'priceData')
    .flatMap((item) => item.contents)
    .filter((content) => content.type === 'attributeTable')
    .flatMap((content) => content.data)
    .some((row) => row.label === 'Kaufpreis');

  return isHouse && isValidHouseType(estate.houseType) && hasPurchasePrice;
};
